import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { HouseIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormPhoneInput } from "@components/inputs/form/FormPhoneInput/FormPhoneInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useSubmitElectricityLeads } from "@services/electricityLeads/mutations";
import { type ISubmitElectricityLeads } from "@services/electricityLeads/types";
import { useEnefitConsultationFormSchema } from "./useEnefitConsultationFormSchema";

interface IEnefitConsultationForm extends ISubmitElectricityLeads {
  jointBillServiceId: number;
  agreement: boolean;
}

export const EnefitConsultationBannerModal: React.FC<IUseModalProps> = (
  props,
) => {
  const { t } = useTranslation();
  const modal = useModal(props);

  const { data: customerData } = useCustomerDetailsQuery();

  const { mutateAsync: submitEnefitConsultationRequest } =
    useSubmitElectricityLeads();

  const schema = useEnefitConsultationFormSchema();

  const { control, handleSubmit, setError } = useForm<IEnefitConsultationForm>({
    defaultValues: {
      firstName: customerData?.firstName,
      lastName: customerData?.lastName,
      email: customerData?.email,
      phoneNumber: customerData?.phone,
      jointBillServiceId: 0,
      panelPlacingLocation: null,
      averagePaidAmount: 0,
      agreement: false,
    },
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitEnefitConsultation = async (data: IEnefitConsultationForm) => {
    await submitEnefitConsultationRequest({
      data,
      jointBillServiceId: data.jointBillServiceId,
    })
      .then(() => {
        toast.success(
          t("electricityLeads.enefitConsultation.modal.toast.success"),
        );
        modal.close();
      })
      .catch(setApiErrors);
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-xl"
      badgeProps={{ icon: HouseIcon }}
      title={t("electricityLeads.enefitConsultation.modal.title")}
      subtitle={t("electricityLeads.enefitConsultation.modal.subtitle")}
      actionButtons={
        <>
          <Button className="w-full" variant="secondary">
            {t("electricityLeads.enefitConsultation.modal.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(submitEnefitConsultation)}
          >
            {t("electricityLeads.enefitConsultation.modal.buttons.send")}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(submitEnefitConsultation)}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            name="firstName"
            control={control}
            label={t(
              "electricityLeads.enefitConsultation.modal.form.fields.firstName.label",
            )}
          />
          <FormInput
            name="lastName"
            control={control}
            label={t(
              "electricityLeads.enefitConsultation.modal.form.fields.lastName.label",
            )}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            name="email"
            control={control}
            label={t(
              "electricityLeads.enefitConsultation.modal.form.fields.email.label",
            )}
          />
          <FormPhoneInput
            name="phoneNumber"
            control={control}
            label={t(
              "electricityLeads.enefitConsultation.modal.form.fields.phoneNumber.label",
            )}
          />
        </div>
        <FormCheckbox
          name="agreement"
          control={control}
          label={
            <Trans
              i18nKey={
                "electricityLeads.enefitConsultation.modal.form.fields.agreement.label"
              }
              components={{
                href: (
                  <Link
                    to="https://www.enefit.lt/.resources/enefit_lt_v2/webresources/assets/pdfs/Enefit-UAB-klient--duomenu-tvarkymo-principai.pdf"
                    target="_blank"
                    className="text-blue-dark hover:underline active:no-underline"
                  ></Link>
                ),
              }}
            />
          }
        />
        <span className="text-sm text-grey-dark">
          {t(
            "electricityLeads.enefitConsultation.modal.form.fields.agreement.disclaimer",
          )}
        </span>
      </form>
    </Modal>
  );
};
